import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, CreditCard, Landmark, Check, FileCheck2, X, Upload, ShieldCheck, ClipboardList,
} from "lucide-react";
import { API } from "../../lib/auth";

export default function Payment() {
  const { tenantId } = useParams();
  const nav = useNavigate();
  const [tenant, setTenant] = useState(null);
  const [method, setMethod] = useState("bank_transfer");
  const [bank, setBank] = useState(null);
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileInput = useRef(null);

  useEffect(() => {
    // we need to find tenant by ID via the payment status endpoint — but that uses reference, not id.
    // workaround: backend provides bank info on select-method call which also returns plan_name + amount
    axios.post(`${API}/auth/payment/select-method`, { tenant_id: tenantId, method: "bank_transfer" })
      .then((r) => {
        setBank(r.data);
        setTenant({ amount: r.data.amount, reference: r.data.reference });
      })
      .catch(() => toast.error("Tenant not found or already processed"));
  }, [tenantId]);

  const onFile = (f) => {
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) return toast.error("File too large (max 5MB)");
    setFile(f);
  };

  const onSubmit = async () => {
    if (method === "paystack") {
      toast.info("Paystack integration coming soon. Please use Bank Transfer.");
      return;
    }
    if (!file) return toast.error("Please upload your proof of payment");
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("tenant_id", tenantId);
      fd.append("file", file);
      const { data } = await axios.post(`${API}/auth/payment/upload-proof`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Payment proof submitted!");
      nav(`/payment-pending/${data.reference}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="payment-page" className="min-h-screen bg-[#FFFCF2] p-6 lg:p-12">
      <div className="max-w-6xl mx-auto">
        <Link to="/register" className="inline-flex items-center gap-2 text-black/60 hover:text-black text-sm mb-7">
          <ArrowLeft size={16} /> Back to registration
        </Link>

        <div className="grid lg:grid-cols-3 gap-7">
          <div className="lg:col-span-2 space-y-6">
            {/* Method selection */}
            <div className="bg-white rounded-2xl border border-black/8 shadow-sm p-7">
              <div className="flex items-center gap-2 pb-4 border-b border-black/8 mb-5">
                <CreditCard size={20} className="text-black/60" />
                <h2 className="font-display font-bold text-xl text-black">Payment Method</h2>
              </div>
              <div className="space-y-3">
                <button data-testid="payment-method-paystack" onClick={() => setMethod("paystack")}
                  className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all text-left ${method === "paystack" ? "border-[#1B4332] bg-[#E8F5E9]/40" : "border-black/10 bg-white hover:border-black/20"}`}>
                  <div className="w-11 h-11 rounded-lg bg-[#E8F5E9] flex items-center justify-center"><Landmark size={20} className="text-[#1B4332]" /></div>
                  <div className="flex-1">
                    <div className="font-semibold text-black flex items-center gap-2">Paystack <span className="text-xs px-2 py-0.5 rounded-full bg-[#FFF9E6] text-[#1B4332]">Coming soon</span></div>
                    <div className="text-sm text-black/60">Pay securely with Paystack</div>
                  </div>
                  <div className={`w-5 h-5 rounded-full border-2 ${method === "paystack" ? "border-[#1B4332] bg-[#1B4332]" : "border-black/30"} flex items-center justify-center`}>
                    {method === "paystack" && <Check size={12} className="text-white" />}
                  </div>
                </button>
                <button data-testid="payment-method-bank" onClick={() => setMethod("bank_transfer")}
                  className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all text-left ${method === "bank_transfer" ? "border-[#1B4332] bg-[#E8F5E9]/40" : "border-black/10 bg-white hover:border-black/20"}`}>
                  <div className="w-11 h-11 rounded-lg bg-[#E8F5E9] flex items-center justify-center"><Landmark size={20} className="text-[#1B4332]" /></div>
                  <div className="flex-1">
                    <div className="font-semibold text-black">Bank Transfer</div>
                    <div className="text-sm text-black/60">Pay via bank transfer and upload proof of payment</div>
                  </div>
                  <div className={`w-5 h-5 rounded-full border-2 ${method === "bank_transfer" ? "border-[#1B4332] bg-[#1B4332]" : "border-black/30"} flex items-center justify-center`}>
                    {method === "bank_transfer" && <Check size={12} className="text-white" />}
                  </div>
                </button>
              </div>
            </div>

            {/* Bank Transfer details */}
            {method === "bank_transfer" && bank && (
              <div className="bg-white rounded-2xl border border-black/8 shadow-sm p-7">
                <div className="flex items-center gap-2 pb-4 border-b border-black/8 mb-5">
                  <Landmark size={20} className="text-black/60" />
                  <h2 className="font-display font-bold text-xl text-black">Bank Transfer Details</h2>
                </div>

                <div className="bg-[#E8F5E9]/40 border border-[#1B4332]/15 rounded-xl p-5 text-center mb-5">
                  <div className="text-sm text-black/60">Amount to Transfer</div>
                  <div className="font-display font-extrabold text-3xl text-[#1B4332] mt-1">₦{Number(bank.amount).toLocaleString()}.00 NGN</div>
                </div>

                <div className="rounded-xl border border-black/10 divide-y divide-black/8">
                  {[
                    { k: "Bank Name", v: bank.bank_name },
                    { k: "Account Holder", v: bank.account_holder },
                    { k: "Account Number", v: bank.account_number },
                    { k: "Reference", v: bank.reference },
                  ].map((r) => (
                    <div key={r.k} className="flex justify-between items-center px-5 py-3.5">
                      <span className="text-sm text-black/60">{r.k}</span>
                      <span className="font-semibold text-black">{r.v}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-5 bg-[#FFF9E6] border border-[#FFE4B0] rounded-xl p-4">
                  <div className="font-semibold text-black mb-1">Instructions</div>
                  <p className="text-sm text-black/70">Payments are confirmed within 20min</p>
                </div>

                {/* File upload */}
                <div className="mt-6">
                  <label className="text-sm font-semibold text-black mb-2 flex items-center gap-2">
                    <Upload size={14} /> Upload Proof of Payment <span className="text-red-500">*</span>
                  </label>
                  <input ref={fileInput} type="file" accept="image/*,application/pdf" hidden
                    onChange={(e) => onFile(e.target.files?.[0])}
                    data-testid="payment-proof-input" />
                  {!file ? (
                    <button onClick={() => fileInput.current?.click()}
                      className="w-full p-8 rounded-xl border-2 border-dashed border-black/15 hover:border-[#1B4332] hover:bg-[#FFFCF2] transition-all flex flex-col items-center justify-center gap-2">
                      <Upload size={28} className="text-black/40" />
                      <span className="text-sm text-black/60">Click to upload (max 5MB)</span>
                    </button>
                  ) : (
                    <div className="w-full p-6 rounded-xl border-2 border-dashed border-[#1B4332]/30 flex flex-col items-center justify-center gap-2">
                      <FileCheck2 size={28} className="text-[#1FA868]" />
                      <span className="text-sm font-semibold text-black">{file.name}</span>
                      <button onClick={() => setFile(null)} className="text-xs text-red-500 inline-flex items-center gap-1 px-3 py-1 rounded border border-red-200">
                        <X size={12} /> Remove
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            <button onClick={onSubmit} disabled={submitting || (method === "bank_transfer" && !file)}
              data-testid="payment-submit"
              className="w-full py-4 rounded-xl bg-gradient-to-r from-[#1FA868] to-[#2D6A4F] text-white font-semibold disabled:opacity-50 hover:opacity-95 transition-opacity">
              {submitting ? "Submitting..." : method === "paystack" ? "Continue with Paystack" : "Submit Payment Proof"}
            </button>
            <p className="text-center text-xs text-black/60 flex items-center justify-center gap-1.5">
              <ShieldCheck size={14} /> Your proof will be reviewed by our team. Workspace is created after approval.
            </p>
          </div>

          {/* Order summary */}
          <div>
            <div className="bg-white rounded-2xl border border-black/8 shadow-sm p-7 sticky top-6">
              <div className="flex items-center gap-2 pb-4 border-b border-black/8 mb-5">
                <ClipboardList size={20} className="text-black/60" />
                <h2 className="font-display font-bold text-xl text-black">Order Summary</h2>
              </div>
              <div className="space-y-3 mb-5">
                <div className="flex justify-between text-sm"><span className="text-black/60">Plan</span><span className="font-semibold text-black">{bank?.plan_name || "Pro"}</span></div>
                <div className="flex justify-between text-sm"><span className="text-black/60">Billing Cycle</span><span className="font-semibold text-black">Monthly</span></div>
              </div>
              <div className="flex justify-between items-center py-4 border-t border-b border-black/10 mb-5">
                <span className="font-semibold text-black">Total</span>
                <span className="font-display font-extrabold text-xl text-[#1B4332]">₦{Number(bank?.amount || 0).toLocaleString()}.00 NGN</span>
              </div>
              <div className="text-xs uppercase tracking-widest font-semibold text-black/50 mb-3">Included</div>
              <ul className="space-y-2.5">
                {["500 Products", "10 Users", "5 Warehouses", "500 Customers", "50 Suppliers", "Point of Sale", "Online Orders", "HR Management", "Accounting", "Transfers"].map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm text-black/85">
                    <Check size={14} className="text-[#1FA868]" /> {f}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
